import { Calendar, FileText, Globe, LayoutDashboard, Mail, Users } from "lucide-react";

/**
 * The enterprise Registration dashboard's tab config and URL scheme — owned here (not by either
 * `enterprise-sidebar` or `landing-page-v2`) so both features depend downward on one shared source
 * instead of on each other. The sidebar's drill-down nav, the breadcrumb, the page's own tab bar,
 * and the app router's `[tab]` route all read the same `ENTERPRISE_REGISTRATION_TABS` list and the
 * same path-building/parsing helpers below.
 */

/** Available tabs in the enterprise registration dashboard's vertical drill-down sidebar. */
export const ENTERPRISE_REGISTRATION_TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "landing-pages", label: "Landing Pages", icon: Globe },
  { id: "forms", label: "Forms", icon: FileText },
  { id: "activities", label: "Activities", icon: Users },
  { id: "schedule", label: "Schedule", icon: Calendar },
  { id: "notifications", label: "Communications", icon: Mail },
] as const;

/** Valid enterprise registration tab ID */
export type EnterpriseRegistrationTab = (typeof ENTERPRISE_REGISTRATION_TABS)[number]["id"];

/** One row of {@link ENTERPRISE_REGISTRATION_TABS}. */
export type EnterpriseRegistrationTabConfig = (typeof ENTERPRISE_REGISTRATION_TABS)[number];

/** Tab the Registration page defaults to when the demo "overview" tab is gated off. */
export const REGISTRATION_FALLBACK_TAB: EnterpriseRegistrationTab = "landing-pages";

/** Registration dashboard's base path template, with a literal `{eventId}` placeholder for static
 *  nav configs that get resolved later (see `replaceEventIdInHref` in `enterprise-sidebar`). */
export const REGISTRATION_BASE_HREF = "/dashboard/manage/{eventId}/registration";

/**
 * Templated href for a Registration tab — `{eventId}` placeholder, resolved later. No suffix for
 * the base/"overview" tab.
 *
 * @param tabId - A Registration tab id (e.g. "forms") or "overview" for the base route
 * @returns The templated href
 */
export function registrationTabHref(tabId: string): string {
  return tabId === "overview" ? REGISTRATION_BASE_HREF : `${REGISTRATION_BASE_HREF}/${tabId}`;
}

/**
 * Concrete Registration base path for a specific event (no tab segment).
 *
 * @param eventId - The event whose Registration dashboard to link to
 * @returns The resolved base path
 */
export function registrationPath(eventId: number): string {
  return REGISTRATION_BASE_HREF.replace("{eventId}", String(eventId));
}

/**
 * Resolves the active Registration tab from the current pathname against a specific event's base
 * path — used to sync client tab state with browser back/forward navigation.
 *
 * @param pathname - `window.location.pathname`
 * @param eventId - The event whose Registration base path to match against
 * @returns The matched tab id, or `REGISTRATION_FALLBACK_TAB` when the trailing segment doesn't
 *   match any known tab id
 */
export function parseRegistrationTab(pathname: string, eventId: number): EnterpriseRegistrationTab {
  const basePath = registrationPath(eventId);
  const segment =
    (pathname.startsWith(basePath) ? pathname.slice(basePath.length).replace(/^\/+/, "") : "") || "overview";
  const match = ENTERPRISE_REGISTRATION_TABS.find((tab) => tab.id === segment);
  return match ? match.id : REGISTRATION_FALLBACK_TAB;
}
